/* Evol Vault pack-simulator engine — pure and DOM-free.
   Consumes the per-set JSON emitted by scripts/build-sim-data.py and samples
   packs from the embedded slot model. The same file runs in the browser
   (window.EvolSimEngine) and in Node for the Monte Carlo tests
   (scripts/test-sim-engine.js) — keep it dependency-free and side-effect-free.

   Rates provenance: the slot models aggregate-match the verified per-pack
   tier rates published on /pages/pokemon-pull-rate-calculator. */
(function (root) {
  'use strict';

  /* ar/sr/sar/mur are the JAPANESE secret tiers (Storm Emeralda m6+); they share
     ranks with their English analogues (ir/ur/sir/mhr) — a pack never mixes the
     two ladders, so the ties are unreachable and hit/tell/sfx thresholds carry
     over unchanged.
     30th Celebration (30c) adds pk (Pikachu Rare: one in EVERY pack, so it ranks
     with the rares and is never a "hit"), cc (Classic Collection reprint, ~1 in
     10, between IR and SIR by rarity and value), fur (Futuristic Rare, ~1 in 100,
     ranks with Hyper Rare) and rgb (RGB Rare Mew, rarer than anything else; no
     slot probability is published yet, so the engine never draws it). */
  var TIER_RANK = {
    c: 0, u: 1, r: 2, pk: 2, rev: 3, asr: 4, dr: 5, ir: 6, ar: 6, cc: 7, mar: 7, ur: 8, sr: 8,
    sir: 9, sar: 9, hr: 10, fur: 10, mhr: 11, mur: 11, rgb: 12,
    /* Scarlet & Violet shiny (Paldean Fates) */
    shr: 5, shur: 8,
    /* Sword & Shield era: a V ranks with Double Rare (the era's "ex"); the
       gallery subsets (Trainer Gallery, Galarian Gallery) and Radiant /
       Amazing Rares sit with Illustration Rare; alt-art Vs and Rainbows with
       SIR; gold secrets with Hyper Rare; the alt-art VMAX (Moonbreon) at the
       top. vmv is the calculator's VMAX-or-VSTAR alias (display only). */
    v: 5, shv: 5, vmax: 6, vstar: 6, vmv: 6, amz: 6, rad: 6, tg: 6, tgc: 6, ggc: 6,
    fa: 7, fat: 7, ru: 7, shvv: 7, tgv: 8, ggv: 8, shvm: 8,
    altv: 9, rr: 9, gold: 10, tggold: 10, gggold: 10, sec: 10, altvmax: 11,
    /* Wizards era (Base Set to Team Rocket): the holo rare is the hit; basic
       Energy (Base Set, Base Set 2: two per pack) ranks below the commons, so it
       flips first and is never a hit */
    holo: 6, e: -1
  };
  var TIER_LABEL = {
    c: 'Common', u: 'Uncommon', r: 'Rare', pk: 'Pikachu Rare', rev: 'Reverse Holo', asr: 'ACE SPEC Rare',
    dr: 'Double Rare', ir: 'Illustration Rare', ar: 'Art Rare', cc: 'Classic Collection', mar: 'Mega Attack Rare',
    ur: 'Ultra Rare', sr: 'Super Rare', sir: 'Special Illustration Rare',
    sar: 'Special Art Rare', hr: 'Hyper Rare', fur: 'Futuristic Rare', mhr: 'Mega Hyper Rare',
    mur: 'Mega Ultra Rare', rgb: 'RGB Rare',
    shr: 'Shiny Rare', shur: 'Shiny Ultra Rare',
    v: 'Pokémon V', vmax: 'VMAX', vstar: 'VSTAR', vmv: 'VMAX or VSTAR', amz: 'Amazing Rare',
    rad: 'Radiant Rare', tg: 'Trainer Gallery', tgc: 'Trainer Gallery holo', tgv: 'Trainer Gallery V or Trainer',
    tggold: 'Trainer Gallery gold', ggc: 'Galarian Gallery holo', ggv: 'Galarian Gallery V, VSTAR or Trainer',
    gggold: 'Galarian Gallery gold VSTAR', fa: 'Full Art', fat: 'Full Art Trainer', ru: 'Full Art Trainer',
    shv: 'Shiny Vault', shvv: 'Shiny Vault V', shvm: 'Shiny Vault VMAX', altv: 'Alternate Art V',
    altvmax: 'Alternate Art VMAX', rr: 'Rainbow Rare', gold: 'Gold Secret Rare', sec: 'Secret Rare',
    holo: 'Holo Rare', e: 'Energy'
  };
  var HIT_MIN_RANK = TIER_RANK.asr; /* "hit" = ACE SPEC / Double Rare or better */

  /* Deterministic RNG for tests; browser passes Math.random */
  function mulberry32(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* Group card indices into tier buckets; "rev" = union of revPool tiers */
  function buildPools(setData) {
    var pools = {};
    for (var i = 0; i < setData.cards.length; i++) {
      var tier = setData.cards[i][2];
      (pools[tier] = pools[tier] || []).push(i);
    }
    var rev = [];
    for (var j = 0; j < setData.pack.revPool.length; j++) {
      var t = setData.pack.revPool[j];
      if (pools[t]) rev = rev.concat(pools[t]);
    }
    pools.rev = rev;
    return pools;
  }

  function pickBucket(pick, rng) {
    var x = rng(), acc = 0;
    for (var i = 0; i < pick.length; i++) {
      acc += pick[i][1];
      if (x < acc) return pick[i][0];
    }
    return pick[pick.length - 1][0]; /* absorb float remainder */
  }

  function drawIndex(pool, used, rng) {
    for (var tries = 0; tries < 100; tries++) {
      var idx = pool[Math.floor(rng() * pool.length)];
      if (!used[idx]) { used[idx] = true; return idx; }
    }
    for (var i = 0; i < pool.length; i++) {
      if (!used[pool[i]]) { used[pool[i]] = true; return pool[i]; }
    }
    return pool[Math.floor(rng() * pool.length)]; /* pool exhausted (never in practice) */
  }

  function makeCard(setData, idx, isRev) {
    var c = setData.cards[idx];
    /* c[3] (optional) = TCGPlayer productId for JP sets, whose image CDN is
       keyed by product rather than card number; the UI's cardN() prefers it.
       c[4] (optional) = full per-card image URL override (JP paired-Stadium
       halves hosted on Shopify Files) — wins over both templates. */
    return { idx: idx, num: c[0], name: c[1], tier: c[2], pid: c[3], img: c[4], rev: !!isRev };
  }

  function displayRank(card) {
    return card.rev ? TIER_RANK.rev : TIER_RANK[card.tier];
  }

  function sortPack(cards) {
    /* stable: rarest at back (last flip) */
    var keyed = cards.map(function (c, i) { return { c: c, i: i }; });
    keyed.sort(function (a, b) {
      var d = displayRank(a.c) - displayRank(b.c);
      return d !== 0 ? d : a.i - b.i;
    });
    for (var i = 0; i < keyed.length; i++) cards[i] = keyed[i].c;
  }

  function openPack(setData, pools, rng) {
    var used = {};
    var cards = [];
    var god = setData.pack.god;
    if (god && rng() < god.p) {
      for (var g = 0; g < god.comp.length; g++) {
        for (var k = 0; k < god.comp[g][1]; k++) {
          cards.push(makeCard(setData, drawIndex(pools[god.comp[g][0]], used, rng), false));
        }
      }
      sortPack(cards);
      return { cards: cards, god: true, best: cards[cards.length - 1] };
    }
    var slots = setData.pack.slots;
    for (var s = 0; s < slots.length; s++) {
      for (var n = 0; n < slots[s].n; n++) {
        var bucket = pickBucket(slots[s].pick, rng);
        var isRev = !!slots[s].rev && bucket === 'rev';
        cards.push(makeCard(setData, drawIndex(pools[bucket], used, rng), isRev));
      }
    }
    sortPack(cards);
    return { cards: cards, god: false, best: cards[cards.length - 1] };
  }

  function openProduct(setData, pools, packCount, rng) {
    var packs = [];
    for (var i = 0; i < packCount; i++) packs.push(openPack(setData, pools, rng));
    return packs;
  }

  function isHit(card) {
    return !card.rev && TIER_RANK[card.tier] >= HIT_MIN_RANK;
  }

  /* Colour family per hit tier: drives the anticipation tell, the card glow,
     rail rings, hit-line and ladder colours and the share card. One table, so
     a new era's tiers are styled by adding them here. '' = not a hit colour. */
  var TIER_GLOW = {
    dr: 'white', v: 'white', shv: 'white', shr: 'white',
    ur: 'blue', mar: 'blue', asr: 'blue', sr: 'blue', shur: 'blue',
    vmax: 'blue', vstar: 'blue', vmv: 'blue', amz: 'blue', rad: 'blue', tg: 'blue', tgc: 'blue', ggc: 'blue',
    fa: 'blue', fat: 'blue', ru: 'blue', shvv: 'blue',
    ir: 'purple', ar: 'purple', cc: 'purple', sir: 'purple', sar: 'purple',
    tgv: 'purple', ggv: 'purple', shvm: 'purple', altv: 'purple', holo: 'purple',
    hr: 'gold', fur: 'gold', mhr: 'gold', mur: 'gold', rgb: 'gold',
    rr: 'gold', gold: 'gold', tggold: 'gold', gggold: 'gold', sec: 'gold', altvmax: 'gold'
  };
  function glowFor(tier) { return TIER_GLOW[tier] || ''; }

  /* Anticipation-tell colour for a pack: gold > purple > blue > white */
  function tellFor(pack) {
    if (pack.god) return 'gold';
    var g = glowFor(pack.best.tier);
    return g && g !== 'white' ? g : 'white';
  }

  var EvolSimEngine = {
    TIER_RANK: TIER_RANK,
    TIER_LABEL: TIER_LABEL,
    mulberry32: mulberry32,
    buildPools: buildPools,
    openPack: openPack,
    openProduct: openProduct,
    isHit: isHit,
    glowFor: glowFor,
    TIER_GLOW: TIER_GLOW,
    tellFor: tellFor,
    displayRank: displayRank
  };

  root.EvolSimEngine = EvolSimEngine;
  if (typeof module !== 'undefined' && module.exports) module.exports = EvolSimEngine;
})(typeof window !== 'undefined' ? window : globalThis);
